import { useCurrentInventoryId } from "../url";
import { useCurrentDatabaseManager, useInventoryDatabases } from "./useDatabases";
import { useCallback } from "react";
import * as uuid from "uuid";
import { usePouchBulkChange } from "./usePouchBulkChange";
import { filterUndefined } from "../../utils/utils";
import { InventoryObject, useCurrentObject } from "./useCurrentObject";
import { isUnsavedImageObject } from "../../utils/image";
import { InventoryFormdataItem } from "./types/inventories";
import { PouchDocument } from "./common";

async function mapAsync<Input, Output>(input: Input[], func: (input: Input, index: number) => Promise<Output>) {
  return await Promise.all(input.map(func));
}
async function mapValuesAsync<Input, Output>(input: Record<string, Input>, func: (input: Input, key: string) => Promise<Output>) {
  return Object.fromEntries(await Promise.all(Object.entries(input).map(async ([key, value]) => [key, await func(value, key)])));
}

export function useUpdateObject() {
  const { inventoryId } = useCurrentInventoryId();
  const { inventory } = useInventoryDatabases();

  const {
    execute: executeImpl,
    working,
    error,
  } = usePouchBulkChange(inventory, inventoryId === undefined ? [] : [`inventory:${inventoryId}`]);

  const mngr = useCurrentDatabaseManager();

  const execute = useCallback(
    async (object: InventoryObject) => {
      if (!mngr || !inventoryId) {
        throw new Error("Database manager and inventory id required");
      }

      const forms = await Promise.all(
        object.data
          // ensure that all forms have an ID, since we need it to reference them
          .map((f) => (f._id ? f : { ...f, _id: uuid.v4() }))
          // store images and replace the unsaved uri-containing objects with the id-containing objects
          .map(async (f) => ({
            ...f,
            values: await mapValuesAsync(f.values, async (value) => ({
              ...value,
              value:
                value.type === "image" && value.value
                  ? await mapAsync(value.value, async (img) =>
                      isUnsavedImageObject(img) ? await mngr.saveImage(inventoryId, f._id!, img) : img
                    )
                  : value.value,
            })),
          }))
      );
      return await executeImpl([
        // replace form objects by their ID, undoing what's being done in `useCurrentObject`
        {
          ...object,
          data: filterUndefined(forms.map((f) => f._id)),
        },
        ...forms,
      ]);
    },
    [executeImpl, inventoryId, mngr]
  );

  return {
    execute,
    working,
    error,
  };
}

export function useCreateObjectForm() {
  const { execute: updateObject, working, error } = useUpdateObject();
  const { data: object, loading } = useCurrentObject();

  const execute = useCallback(
    async (form: PouchDocument<InventoryFormdataItem>) => {
      if (!object) {
        return;
      }
      await updateObject({
        ...object,
        data: [...object.data, form],
      });
    },
    [updateObject, object]
  );

  return { execute, working: working || loading, error };
}
